// src/components/ErrorBoundary.jsx
// ✅ GAP-UX-001 (Apr 2, 2026): Error Boundary Component
//
// WHY THIS EXISTS:
//   Without an Error Boundary, any uncaught JS render error in any component
//   causes the entire React tree to unmount → blank white screen for the user.
//   This component catches those errors and renders a recovery UI instead.
//
// WHAT IT DOES:
//   - Catches all render-phase errors in its child tree (React class lifecycle)
//   - Shows a friendly error screen with "Try Again" and "Go to Dashboard" actions
//   - Logs errors to console (replace with Sentry/analytics in production)
//   - Supports route-level isolation: wrap individual pages to contain crashes
//   - Supports app-level protection: wraps entire <App /> as last-resort fallback
//   - `resetOnNavigate` prop: auto-resets boundary when the URL changes
//     (prevents a crashed page from staying crashed after the user navigates away)
//
// USAGE:
//   1. App-level (in App.js — catches everything):
//      <RootErrorBoundary><AppRoutes /></RootErrorBoundary>
//
//   2. Route-level (isolates a single page crash):
//      <ErrorBoundary context="CaseDetail"><CaseDetail /></ErrorBoundary>
//
//   3. Component-level (isolates a widget):
//      <ErrorBoundary context="InvoiceTable" minimal><InvoiceTable /></ErrorBoundary>
//
// ✅ File Update Reminder: If you add new error reporting (e.g. Sentry),
//    update componentDidCatch() below.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { AlertTriangle, RefreshCw, Home, ChevronDown, ChevronUp } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// ERROR BOUNDARY CLASS COMPONENT
// Must be a class component — React does not support error boundaries
// in functional components (hooks cannot catch render errors).
// ─────────────────────────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError:      false,
      error:         null,
      errorInfo:     null,
      showDetails:   false,   // toggle for dev stack trace panel
      errorId:       null,    // unique ID for support reference
    };
    this.handleReset = this.handleReset.bind(this);
  }

  // ── 1. Derive state from error (runs before render) ──────────────────────
  static getDerivedStateFromError(error) {
    // Generate a short ID for support cross-referencing
    const errorId = `ERR-${Date.now().toString(36).toUpperCase()}`;
    return {
      hasError: true,
      error,
      errorId,
    };
  }

  // ── 2. Capture component stack (runs after render, for side-effects) ─────
  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // ── Error Reporting ──────────────────────────────────────────────────
    // TODO (BS-007 Observability): Replace console.error with Sentry or
    //      your Cloud Function error-reporting endpoint.
    //
    //   Sentry example:
    //     import * as Sentry from '@sentry/react';
    //     Sentry.captureException(error, { extra: errorInfo });
    //
    //   Custom endpoint example:
    //     fetch('/api/log-error', {
    //       method: 'POST',
    //       body: JSON.stringify({ error: error.message, stack: errorInfo.componentStack, context: this.props.context })
    //     });
    // ────────────────────────────────────────────────────────────────────

    console.error(
      `[ErrorBoundary] Caught error in "${this.props.context || 'unknown'}" — ID: ${this.state.errorId}`,
      '\nError:', error,
      '\nComponent Stack:', errorInfo?.componentStack
    );
  }

  // ── 3. Reset boundary state (called by "Try Again" button) ───────────────
  handleReset() {
    this.setState({
      hasError:    false,
      error:       null,
      errorInfo:   null,
      showDetails: false,
      errorId:     null,
    });
    // If consumer passed an onReset callback, call it too
    if (typeof this.props.onReset === 'function') {
      this.props.onReset();
    }
  }

  // ── 4. Auto-reset when URL changes (resetOnNavigate prop) ────────────────
  componentDidUpdate(prevProps) {
    if (
      this.state.hasError &&
      this.props.resetOnNavigate &&
      prevProps.location !== this.props.location
    ) {
      this.handleReset();
    }
  }

  render() {
    const { hasError, error, errorInfo, showDetails, errorId } = this.state;
    const { children, minimal, context } = this.props;

    if (!hasError) return children;

    // ── MINIMAL fallback: for widget/component-level boundaries ──────────
    if (minimal) {
      return (
        <div className="w-full rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-red-800">
                {context ? `${context} failed to load` : 'This section failed to load'}
              </p>
              <p className="text-xs text-red-600 mt-0.5">
                {error?.message || 'An unexpected error occurred.'}
              </p>
            </div>
            <button
              onClick={this.handleReset}
              className="flex-shrink-0 flex items-center gap-1.5 text-xs font-medium text-red-700 hover:text-red-900 bg-white border border-red-200 rounded px-2.5 py-1.5 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        </div>
      );
    }

    // ── FULL-PAGE fallback: for page/app-level boundaries ────────────────
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="w-full max-w-lg">

          {/* ── Icon + Heading ── */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Something went wrong
            </h1>
            <p className="text-slate-600 text-sm leading-relaxed">
              An unexpected error occurred{context ? ` in ${context}` : ''}.
              Your data is safe — this is a display error only.
            </p>
          </div>

          {/* ── Error Card ── */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mb-6">

            {/* Error message row */}
            <div className="px-5 py-4 border-b border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
                Error
              </p>
              <p className="text-sm text-slate-800 font-medium">
                {error?.message || 'Unknown render error'}
              </p>
            </div>

            {/* Reference ID row */}
            {errorId && (
              <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                <p className="text-xs text-slate-500">Reference ID</p>
                <code className="text-xs font-mono text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                  {errorId}
                </code>
              </div>
            )}

            {/* Developer stack trace toggle (only shown in development) */}
            {process.env.NODE_ENV === 'development' && errorInfo?.componentStack && (
              <div className="border-t border-slate-100">
                <button
                  onClick={() => this.setState(s => ({ showDetails: !s.showDetails }))}
                  className="w-full px-5 py-3 flex items-center justify-between text-xs text-slate-500 hover:bg-slate-50 transition-colors"
                >
                  <span className="font-medium">Developer Details</span>
                  {showDetails
                    ? <ChevronUp className="w-3.5 h-3.5" />
                    : <ChevronDown className="w-3.5 h-3.5" />
                  }
                </button>
                {showDetails && (
                  <div className="px-5 pb-4">
                    <pre className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-3 overflow-auto max-h-40 whitespace-pre-wrap break-all">
                      {errorInfo.componentStack}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Action Buttons ── */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={this.handleReset}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Try Again
            </button>
            <button
              onClick={() => {
                this.handleReset();
                window.location.href = '/';
              }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg border border-slate-200 transition-colors"
            >
              <Home className="w-4 h-4" />
              Go to Dashboard
            </button>
          </div>

          {/* ── Support note ── */}
          <p className="text-center text-xs text-slate-400 mt-6">
            If this keeps happening, contact support with reference ID{' '}
            <span className="font-mono">{errorId}</span>.
          </p>
        </div>
      </div>
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ROUTE-AWARE WRAPPER
// Wraps ErrorBoundary with location prop from React Router so that
// resetOnNavigate works correctly. Use this version inside <Routes>.
// ─────────────────────────────────────────────────────────────────────────────
export function RouteErrorBoundary({ children, context }) {
  // We use a key trick: when the pathname changes, React remounts
  // this component entirely, which resets the error state automatically.
  // This is simpler and more reliable than listening to location changes.
  return (
    <ErrorBoundary context={context} resetOnNavigate>
      {children}
    </ErrorBoundary>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT ERROR BOUNDARY (App-level last resort)
// Wraps the entire app. Uses a slightly different message since at this
// level we don't know which page crashed.
// ─────────────────────────────────────────────────────────────────────────────
export class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      errorId: `ERR-${Date.now().toString(36).toUpperCase()}`,
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[RootErrorBoundary] Fatal app crash:', error, errorInfo);
    // TODO: Sentry.captureException(error, { extra: errorInfo });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="w-20 h-20 bg-red-100 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-3">
            LexCore LPMS has crashed
          </h1>
          <p className="text-slate-600 text-sm mb-2">
            A critical error occurred. Your data is safe — only the display is affected.
          </p>
          {this.state.errorId && (
            <p className="text-xs text-slate-400 mb-8">
              Reference: <span className="font-mono">{this.state.errorId}</span>
            </p>
          )}
          <button
            onClick={() => window.location.reload()}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Reload Application
          </button>
          <p className="text-xs text-slate-400 mt-6">
            If this persists, please contact support.
          </p>
        </div>
      </div>
    );
  }
}

// Default export is the general-purpose ErrorBoundary
export default ErrorBoundary;
